import { Input } from "@/components/ui/input";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

function Field({ label, value, onChange, step = 1, testId, hint, unit }) {
  return (
    <div>
      <div className="label-caps flex items-center gap-2">{label}
        {hint && <span className="normal-case tracking-normal text-[10px] text-slate-400 inline-flex items-center gap-1"><Info className="h-3 w-3" /> {hint}</span>}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Input type="number" step={step} min={0} value={value ?? 0} onChange={(e) => onChange(parseFloat(e.target.value || "0"))} className="max-w-[220px]" data-testid={testId} />
        {unit && <span className="text-slate-500 text-sm">{unit}</span>}
      </div>
    </div>
  );
}

export default function Step7Roi({ value, onChange }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").split("-")[0];
  const hyp = lang === "en" ? "assumption" : "hypothèse";
  const hypCentral = lang === "en" ? "central assumption" : "hypothèse centrale";
  const hypExcl = lang === "en" ? "excluded by default" : "exclu par défaut";
  const uSem = lang === "en" ? "wks" : "sem";
  const uCas = lang === "en" ? "cases" : "cas";
  const uAns = lang === "en" ? "yrs" : "ans";
  const set = (patch) => onChange(patch);
  return (
    <div className="py-8 space-y-10" data-testid="step-7">
      <p className="text-slate-600 leading-relaxed max-w-3xl">
        {t("wizard.s7_intro")}
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <Field label={t("wizard.s7_rate")} value={value.hourly_rate} onChange={(v) => set({ hourly_rate: v })} testId="roi-rate" hint={hyp} unit="€/h" />
        <Field label={t("wizard.s7_weekly")} value={value.weekly_monitoring_hours} onChange={(v) => set({ weekly_monitoring_hours: v })} testId="roi-weekly" unit="h" />
        <Field label={t("wizard.s7_weeks")} value={value.weeks_per_year} onChange={(v) => set({ weeks_per_year: v })} testId="roi-weeks" hint={hyp} unit={uSem} />
        <Field label={t("wizard.s7_audit_h")} value={value.audit_hours_per_year} onChange={(v) => set({ audit_hours_per_year: v })} testId="roi-audit-hours" unit="h" />
        <Field label={t("wizard.s7_adhoc")} value={value.auditor_ad_hoc_hours} onChange={(v) => set({ auditor_ad_hoc_hours: v })} testId="roi-adhoc-hours" unit="h" />
        <Field label={t("wizard.s7_inv")} value={value.investigations_per_year} onChange={(v) => set({ investigations_per_year: v })} testId="roi-investigations" unit={uCas} />
        <Field label={t("wizard.s7_inv_cur")} value={value.investigation_hours_current} onChange={(v) => set({ investigation_hours_current: v })} testId="roi-inv-current" unit="h" />
        <Field label={t("wizard.s7_inv_tgt")} value={value.investigation_hours_target} onChange={(v) => set({ investigation_hours_target: v })} step={0.5} testId="roi-inv-target" hint={hyp} unit="h" />
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-900">{t("wizard.s7_risk_title")}</h3>
        <p className="mt-1 text-sm text-slate-500">{t("wizard.s7_risk_sub")}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-4">
          <Field label={t("wizard.s7_incidents")} value={value.incidents_3_years} onChange={(v) => set({ incidents_3_years: v })} testId="roi-incidents" />
          <Field label={t("wizard.s7_incident_cost")} value={value.avg_incident_cost} onChange={(v) => set({ avg_incident_cost: v })} testId="roi-incident-cost" unit="€" />
          <Field label={t("wizard.s7_prob")} value={value.annual_incident_probability} step={0.01} onChange={(v) => set({ annual_incident_probability: v })} testId="roi-prob" unit="0–1" />
          <Field label={t("wizard.s7_reduction")} value={value.risk_reduction_pct} step={0.01} onChange={(v) => set({ risk_reduction_pct: v })} testId="roi-reduction" hint={hypCentral} unit="0–1" />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-900">{t("wizard.s7_fin_title")}</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-4">
          <Field label={t("wizard.s7_years")} value={value.analysis_years} onChange={(v) => set({ analysis_years: v })} testId="roi-years" unit={uAns} />
          <Field label={t("wizard.s7_discount")} value={value.discount_rate} step={0.01} onChange={(v) => set({ discount_rate: v })} testId="roi-discount" unit="0–1" />
          <Field label={t("wizard.s7_growth")} value={value.identity_growth_pct} step={0.01} onChange={(v) => set({ identity_growth_pct: v })} testId="roi-growth" unit="0–1" />
          <Field label={t("wizard.s7_run")} value={value.run_cost_annual} onChange={(v) => set({ run_cost_annual: v })} testId="roi-run" unit="€" />
          <Field label={t("wizard.s7_mob")} value={value.internal_mobilization_cost} onChange={(v) => set({ internal_mobilization_cost: v })} testId="roi-mobilization" unit="€" />
          <Field label={t("wizard.s7_ins")} value={value.insurance_premium_reduction} onChange={(v) => set({ insurance_premium_reduction: v })} testId="roi-insurance-reduction" hint={hypExcl} unit="€" />
        </div>
      </div>

      <div className="rounded-sm border border-amber-200 bg-amber-50/60 p-4 text-sm text-slate-700">
        <b>{lang === "en" ? "Methodological note." : "Note méthodologique."}</b> {t("wizard.s7_note")}
      </div>
    </div>
  );
}

export const step7Valid = (v) => v.hourly_rate > 0 && v.weeks_per_year > 0 && v.analysis_years > 0;
